import * as XLSX from 'xlsx';

export interface ColumnDef {
  header: string;  // Excel column header — also used as key when parsing rows
  example: string; // Example value shown in row 2 of the template
}

/**
 * Download a blank .xlsx template with headers in row 1 and examples in row 2.
 */
export function downloadTemplate(moduleName: string, columns: ColumnDef[]): void {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([
    columns.map((c) => c.header),
    columns.map((c) => c.example),
  ]);

  // Column widths — auto-size to header + example length
  ws['!cols'] = columns.map((c) => ({
    wch: Math.max(c.header.length, c.example.length) + 4,
  }));

  XLSX.utils.book_append_sheet(wb, ws, 'Template');
  XLSX.writeFile(wb, `${moduleName.toLowerCase().replace(/\s+/g, '-')}-template.xlsx`);
}

/**
 * Download records as .xlsx with headers in row 1, data starting row 2.
 */
export function downloadData(
  moduleName: string,
  columns: ColumnDef[],
  rows: Record<string, string>[]
): void {
  const wb = XLSX.utils.book_new();
  const dataRows = rows.map((row) => columns.map((c) => row[c.header] ?? ''));
  const ws = XLSX.utils.aoa_to_sheet([columns.map((c) => c.header), ...dataRows]);

  ws['!cols'] = columns.map((c) => ({ wch: Math.max(c.header.length + 4, 16) }));

  XLSX.utils.book_append_sheet(wb, ws, 'Export');
  XLSX.writeFile(wb, `${moduleName.toLowerCase().replace(/\s+/g, '-')}-export.xlsx`);
}

/**
 * Parse an uploaded .xlsx file.
 * Row 1 = headers, Row 2 = example (skipped), Row 3+ = actual data.
 * Returns an array of objects keyed by column header.
 */
export function parseExcelFile(file: File): Promise<Record<string, string>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (raw.length < 3) {
          resolve([]); // No data rows (only header + example)
          return;
        }

        const headers: string[] = (raw[0] as string[]).map((h) => String(h).trim());
        // raw[1] is the example row — skip it
        const dataRows = raw.slice(2);

        const result = dataRows
          .map((row: any[]) => {
            const obj: Record<string, string> = {};
            headers.forEach((h, i) => {
              obj[h] = String(row[i] ?? '').trim();
            });
            return obj;
          })
          .filter((row) => Object.values(row).some((v) => v !== ''));

        resolve(result);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });
}
